// Competition Info tab — the brief + withdraw control. Reads competition
// and deliverables from useCompetitionShell; owns only the withdraw
// confirmation state locally.

import { useState } from "react";
import { Platform, Pressable, ScrollView, Text, View } from "react-native";

import { COLORS, FONTS } from "../../../../../lib/brand";
import { PANE_THEMES } from "../../../../../components/brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;

import { CompactWorkspaceHero } from "../../../../../components/student/dashboard/CompactWorkspaceHero";
import { WorkspaceInfoView } from "../../../../../components/student/dashboard/WorkspaceInfoView";
import { useCompetitionShell } from "../../../../../components/student/CompetitionShellContext";
import { withdrawRegistration } from "../../../../../lib/registrations";

export default function StudentCompetitionInfoScreen() {
  const {
    competitionId,
    competition,
    deliverables,
    teamSubmissions,
    hasLoaded,
    registered,
    busy,
    setBusy,
    actionErr,
    setActionErr,
    refresh,
  } = useCompetitionShell();

  async function onWithdrawDirect() {
    if (!competitionId) return;
    setBusy(true);
    setActionErr(null);
    const { error } = await withdrawRegistration(competitionId);
    setBusy(false);
    if (error) {
      setActionErr(error.message);
      return;
    }
    await refresh();
  }

  if (!competition) return null;
  const row = competition;

  return (
    <ScrollView className="flex-1 pb-10" keyboardShouldPersistTaps="handled">
      <CompactWorkspaceHero
        companyName={row.company_name ?? "Company"}
        programName={row.name}
        opensAt={row.opens_at}
        closesAt={row.closes_at}
        status={row.status}
        teamSizeMin={row.team_size_min}
        teamSizeMax={row.max_team_size}
        deliverablesCount={deliverables.length}
        submittedCount={teamSubmissions.length}
        nextDueAt={
          [...deliverables]
            .filter((d) => !teamSubmissions.some((s) => s.deliverable_id === d.id))
            .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))[0]?.due_at ?? null
        }
      />

      <View className="px-4 py-6 md:px-8">
        <View style={{ marginBottom: 18 }}>
          <Text
            className="text-[10px] font-bold uppercase tracking-widest-3 text-amber"
            style={{ fontFamily: FONTS.bodyBold, letterSpacing: 2 }}
          >
            The brief
          </Text>
          <Text
            className="mt-1 text-[28px] leading-tight text-text"
            style={{ fontFamily: FONTS.display, letterSpacing: -0.2 }}
          >
            What you&apos;re{" "}
            <Text
              style={{
                fontStyle: "italic",
                color: COLORS.AMBER.hex,
                fontFamily: FONTS.display,
              }}
            >
              solving.
            </Text>
          </Text>
        </View>

        <WorkspaceInfoView competition={row} deliverables={deliverables} />

        {hasLoaded && registered ? (
          <WithdrawSection
            busy={busy}
            actionErr={actionErr}
            onConfirm={() => void onWithdrawDirect()}
          />
        ) : null}
      </View>
    </ScrollView>
  );
}

/**
 * Withdraw-from-competition section. Two-click confirm — Alert.alert is a
 * no-op on web (see CLAUDE.md), so this is the canonical pattern for
 * destructive student actions in the v2 portal: first click on "Withdraw"
 * expands an inline confirm card; the second click fires the action.
 */
function WithdrawSection({
  busy,
  actionErr,
  onConfirm,
}: {
  busy: boolean;
  actionErr: string | null;
  onConfirm: () => void;
}) {
  const [confirming, setConfirming] = useState(false);
  return (
    <View style={{ marginTop: 32 }}>
      <View
        style={{
          height: 1,
          backgroundColor: STUDENT_THEME.border,
          marginBottom: 22,
        }}
      />
      <Text
        style={{
          fontFamily: FONTS.bodyBold,
          fontSize: 10.5,
          letterSpacing: 2.1,
          textTransform: "uppercase",
          color: STUDENT_THEME.textOnSurfaceDim,
          marginBottom: 12,
        }}
      >
        Withdraw from program
      </Text>

      {!confirming ? (
        <View style={{ flexDirection: "row", flexWrap: "wrap", alignItems: "center", gap: 14 }}>
          <Pressable
            onPress={() => setConfirming(true)}
            disabled={busy}
            accessibilityRole="button"
            accessibilityLabel="Withdraw from this competition"
            style={({ hovered }) => [
              {
                paddingVertical: 10,
                paddingHorizontal: 18,
                borderRadius: 5,
                borderWidth: 1,
                borderColor:
                  hovered && Platform.OS === "web"
                    ? "rgba(192,57,43,0.55)"
                    : "rgba(192,57,43,0.30)",
                backgroundColor:
                  hovered && Platform.OS === "web"
                    ? "rgba(192,57,43,0.06)"
                    : "transparent",
                opacity: busy ? 0.6 : 1,
              },
              Platform.OS === "web"
                ? ({
                    cursor: busy ? "not-allowed" : "pointer",
                    transition: "background-color 0.15s ease, border-color 0.15s ease",
                  } as object)
                : null,
            ]}
          >
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 11,
                letterSpacing: 1.6,
                textTransform: "uppercase",
                color: "#A33D2F",
              }}
            >
              Withdraw
            </Text>
          </Pressable>
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 12.5,
              color: STUDENT_THEME.textOnSurfaceDim,
              flexShrink: 1,
            }}
          >
            Removes your registration and any submissions can no longer be edited.
          </Text>
        </View>
      ) : (
        <View
          style={{
            backgroundColor: "rgba(192,57,43,0.04)",
            borderWidth: 1,
            borderColor: "rgba(192,57,43,0.28)",
            borderRadius: 8,
            paddingVertical: 16,
            paddingHorizontal: 18,
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 10,
              letterSpacing: 1.7,
              textTransform: "uppercase",
              color: "#A33D2F",
              marginBottom: 6,
            }}
          >
            Confirm withdrawal
          </Text>
          <Text
            style={{
              fontFamily: FONTS.displayMedium,
              fontSize: 18,
              lineHeight: 22,
              color: COLORS.FOREST.hex,
              marginBottom: 6,
            }}
          >
            Withdraw from this competition?
          </Text>
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 13,
              lineHeight: 19,
              color: "rgba(28,43,26,0.78)",
              marginBottom: 14,
            }}
          >
            You&apos;ll lose access to the team workspace and any submissions you&apos;ve
            made. This can&apos;t be undone — you&apos;d need to register again.
          </Text>
          <View style={{ flexDirection: "row", gap: 10, flexWrap: "wrap" }}>
            <Pressable
              onPress={() => setConfirming(false)}
              disabled={busy}
              accessibilityRole="button"
              accessibilityLabel="Cancel withdrawal"
              style={[
                {
                  paddingVertical: 9,
                  paddingHorizontal: 16,
                  borderRadius: 5,
                  borderWidth: 1,
                  borderColor: "rgba(28,43,26,0.18)",
                  backgroundColor: "transparent",
                },
                Platform.OS === "web"
                  ? ({ cursor: busy ? "not-allowed" : "pointer" } as object)
                  : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 10.5,
                  letterSpacing: 1.6,
                  textTransform: "uppercase",
                  color: STUDENT_THEME.textOnSurfaceDim,
                }}
              >
                Cancel
              </Text>
            </Pressable>
            <Pressable
              onPress={() => {
                setConfirming(false);
                onConfirm();
              }}
              disabled={busy}
              accessibilityRole="button"
              accessibilityLabel="Confirm withdraw from competition"
              style={[
                {
                  paddingVertical: 9,
                  paddingHorizontal: 16,
                  borderRadius: 5,
                  backgroundColor: busy ? "rgba(163,61,47,0.6)" : "#A33D2F",
                },
                Platform.OS === "web"
                  ? ({ cursor: busy ? "not-allowed" : "pointer" } as object)
                  : null,
              ]}
            >
              <Text
                style={{
                  fontFamily: FONTS.bodyBold,
                  fontSize: 10.5,
                  letterSpacing: 1.6,
                  textTransform: "uppercase",
                  color: STUDENT_THEME.surfaceRaisedBg,
                }}
              >
                Confirm withdraw
              </Text>
            </Pressable>
          </View>
        </View>
      )}

      {actionErr ? (
        <Text
          style={{
            marginTop: 10,
            fontFamily: FONTS.bodyMedium,
            fontSize: 12,
            color: "#A33D2F",
          }}
        >
          {actionErr}
        </Text>
      ) : null}
    </View>
  );
}
